import { OpenInNew } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getSkill from '@src/APIs/graphQl/Skill/getSkill';
import AddSkillButton from '@src/App/Components/Buttons/AddSkillButton/AddSkillButton';
import EmployeesCard from '@src/App/Components/Cards/EntityCards/Employees/EmployeesCard/EmployeesCard';
import ProductsCard from '@src/App/Components/Cards/EntityCards/Products/ProductsCard/ProductsCard';
import RolesCard from '@src/App/Components/Cards/EntityCards/Roles/RolesCard/RolesCard';
import EntityPageInformation, {
    generateAcusitionEntityPageInformation,
} from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { SkillDialog } from '@src/App/Components/Dialogs/SkillDialog/SkillDialog';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import SkillCategorySelect from '@src/App/Components/General/FormularElements/formsControls/inputs/SkillCategorySelect/SkillCategorySelect';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type SkillDetail from '@src/Objects/SkillDetail';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

interface SkillPageProps {
    entity?: SkillDetail;
    inDialog?: boolean;
}

const SkillPage: React.FC<SkillPageProps> = (props) => {
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const { id } = useParams();
    const entityId = props.entity ? props.entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;
    const entity = useSelector((state: AppState) => state.skills.find((a) => a.id === entityId));

    const emp = useSelector((state: AppState) => state.employee);
    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);
    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [activeRole, setActiveRole] = useState('admin');

    useEffect(() => {
        if (props.inDialog) return;
        getSkill(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [props.inDialog, entityId]);

    const skillCategory = entity ? skillCategories.find((cat) => cat.id === entity.categoryId) : null;

    if (!emp) return null;
    const handleOpenEditDialog = async () => {
        return openDialog(<SkillDialog id="SkillDialog" skill={entity} />);
    };

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'skills-overview',
            label: 'Fähigkeiten Katalog',
            icon: getAppIcon(EIconCategory.CATALOG),
            onClick: () => navigate(`/${ELinks.SKILLS}`),
            variant: 'primary',
        },
        {
            id: 'skill-manager',
            label: 'Skill Manager',
            icon: getAppIcon(EIconCategory.SKILLS),
            onClick: () => navigate(`/${ELinks.PROFILE}?tab=5`),
            variant: 'default' as const,
        },
    ];
    return (
        <>
            {!props.inDialog && (
                <MvpSubNav
                    currentPageTitle={entity?.title || 'Skill'}
                    roles={roles}
                    activeRole={activeRole}
                    onRoleChange={setActiveRole}
                    quickActions={quickActions}
                />
            )}
            <EntityPage
                inDialog={props.inDialog}
                loadingError={loadingError}
                entity={entity}
                admin={permissions.admin}
                permissions={permissions.entities.Skills}
                onDelete={() => {
                    if (!props.inDialog) navigate(`/${ELinks.SKILLS}`);
                }}
                buttons={entity ? <AddSkillButton size="small" title={entity.title} id={entity.id} /> : undefined}
                openEditDialog={handleOpenEditDialog}
                informations={
                    entity
                        ? [
                              skillCategory ? (
                                  <EntityPageInformation title={langStrings.skillCategory}>
                                      <SkillCategorySelect
                                          skillCategories={skillCategories}
                                          selectedCategory={skillCategory?.id}
                                          readonly={true}
                                      />
                                  </EntityPageInformation>
                              ) : undefined,
                              entity.skillType ? (
                                  <EntityPageInformation title={langStrings.skillType}>
                                      {entity.skillType}
                                  </EntityPageInformation>
                              ) : undefined,
                              entity.productInfo && entity.productInfo.productName ? (
                                  <EntityPageInformation title={langStrings.productName}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                                          <Typography variant="body2" className="rowElementContent">
                                              {entity.productInfo.productName}
                                          </Typography>
                                          {entity.productInfo.link && (
                                              <IconButton
                                                  title={langStrings.oftenUsed.openInNewTab}
                                                  size="small"
                                                  style={{ padding: 0 }}
                                                  onClick={() => {
                                                      window.open(entity.productInfo?.link, '_blank');
                                                  }}
                                              >
                                                  <OpenInNew fontSize="small" />
                                              </IconButton>
                                          )}
                                      </Box>
                                  </EntityPageInformation>
                              ) : undefined,
                              entity.productInfo && entity.productInfo.manufacturer ? (
                                  <EntityPageInformation title={langStrings.manufacturer}>
                                      {entity.productInfo.manufacturer}
                                  </EntityPageInformation>
                              ) : undefined,
                              entity.description ? (
                                  <EntityPageInformation title={langStrings.description} size={'full'}>
                                      <ExpandableText>
                                          <FormatedTextInput value={entity.description} readonly={true} />
                                      </ExpandableText>
                                  </EntityPageInformation>
                              ) : undefined,
                              generateAcusitionEntityPageInformation(entity, langStrings),
                          ]
                        : []
                }
            >
                {entity && (
                    <>
                        {readRoles && <RolesCard roles={entity.roles} />}
                        <EmployeesCard
                            title={langStrings.employeesWithSkill}
                            employees={entity.employees}
                            skillId={entity.id}
                        />
                        {/* <ProductsCard products={entity.products} /> */}
                    </>
                )}
            </EntityPage>
        </>
    );
};

export default SkillPage;
